/* ============================================================
   DIETMONSTA · Bulk Packs bundle builder

   Filter by type, mix any combination, minimum N packs.
   The minimum is enforced here for UX only — see PROJECT.md.
   ============================================================ */

class DMBulkPacks extends HTMLElement {
  connectedCallback() {
    this.strings = this.#readStrings();
    this.minPacks = parseInt(this.dataset.minPacks, 10) || 0;
    this.filter = ''; // '' matches everything
    this.selection = new Map();

    this.rows = Array.from(this.querySelectorAll('[data-dm-item]'));

    const all = (sel) => Array.from(this.querySelectorAll(sel));
    this.els = {
      filled: all('[data-dm-filled]'),
      total: all('[data-dm-total]'),
      submit: all('[data-dm-submit]'),
      error: all('[data-dm-error]'),
      filters: all('[data-dm-filter]'),
      list: this.querySelector('[data-dm-list]'),
      empty: this.querySelector('[data-dm-empty]'),
      noResults: this.querySelector('[data-dm-no-results]'),
      live: this.querySelector('[data-dm-live]'),
    };

    const first = this.els.filters[0];
    if (first) this.filter = (first.dataset.dmFilter || '').toLowerCase();

    this.#bind();
    this.#applyFilter();
    this.#render();
  }

  #readStrings() {
    const node = this.querySelector('[data-dm-strings]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch {
      return {};
    }
  }

  #bind() {
    this.addEventListener('click', (event) => {
      const filterBtn = event.target.closest('[data-dm-filter]');
      if (filterBtn) {
        this.filter = (filterBtn.dataset.dmFilter || '').toLowerCase();
        this.els.filters.forEach((b) => {
          b.setAttribute('aria-pressed', b === filterBtn ? 'true' : 'false');
        });
        return this.#applyFilter();
      }

      const row = event.target.closest('[data-dm-item]');
      if (row) {
        if (event.target.closest('[data-dm-add], [data-dm-inc]')) return this.#bump(row, +1);
        if (event.target.closest('[data-dm-dec]')) return this.#bump(row, -1);
      }

      if (event.target.closest('[data-dm-submit]')) return this.#submit();
    });
  }

  /* Filtering only hides rows — it never clears the selection. A pack
     stays in the bundle when you switch tabs, which is the whole point of
     mixing across categories. (Custom Prep behaves differently on purpose:
     there, switching goal makes the other goal's add-ons genuinely
     unavailable, so they have to be dropped.) */
  #applyFilter() {
    let visible = 0;
    this.rows.forEach((row) => {
      const tags = (row.dataset.tags || '').split(',').map((t) => t.trim());
      const show = !this.filter || tags.includes(this.filter);
      row.hidden = !show;
      if (show) visible += 1;
    });
    if (this.els.noResults) this.els.noResults.hidden = visible > 0;
  }

  #bump(row, delta) {
    const id = row.dataset.variantId;
    const next = (this.selection.get(id) || 0) + delta;
    if (next < 0) return;
    if (next === 0) this.selection.delete(id);
    else this.selection.set(id, next);
    this.#render();
  }

  #rowFor(id) {
    return this.rows.find((r) => r.dataset.variantId === id);
  }

  #filled() {
    let n = 0;
    this.selection.forEach((qty) => {
      n += qty;
    });
    return n;
  }

  #subtotal() {
    let cents = 0;
    this.selection.forEach((qty, id) => {
      const row = this.#rowFor(id);
      if (row) cents += parseInt(row.dataset.price, 10) * qty;
    });
    return cents;
  }

  #money(cents) {
    const currency = this.strings.currency || 'MYR';
    const value = cents / 100;
    try {
      const parts = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).formatToParts(value);
      // Matches the card price display: whole amounts drop the ".00",
      // fractional ones keep their cents.
      const fraction = parts.find((p) => p.type === 'fraction');
      const dropDecimals = fraction && /^0+$/.test(fraction.value);
      return {
        symbol: parts.filter((p) => p.type === 'currency').map((p) => p.value).join(''),
        amount: parts
          .filter((p) => p.type !== 'currency' && p.type !== 'literal')
          .filter((p) => !dropDecimals || (p.type !== 'decimal' && p.type !== 'fraction'))
          .map((p) => p.value)
          .join(''),
      };
    } catch {
      return { symbol: '', amount: Number.isInteger(value) ? String(value) : value.toFixed(2) };
    }
  }

  #render() {
    const filled = this.#filled();
    const remaining = this.minPacks - filled;

    this.rows.forEach((row) => {
      const qty = this.selection.get(row.dataset.variantId) || 0;
      const addBtn = row.querySelector('[data-dm-add]');
      const stepper = row.querySelector('[data-dm-stepper]');
      const badge = row.querySelector('[data-dm-qty-badge]');

      row.classList.toggle('is-selected', qty > 0);

      if (addBtn && stepper) {
        addBtn.hidden = qty > 0;
        stepper.hidden = qty === 0;
      }
      if (badge) {
        badge.hidden = qty === 0;
        const bv = row.querySelector('[data-dm-qty-badge-val]');
        if (bv) bv.textContent = String(qty);
      }
      const qtyEl = row.querySelector('[data-dm-qty]');
      if (qtyEl) qtyEl.textContent = String(qty);
    });

    this.els.filled.forEach((el) => {
      el.textContent = String(filled);
    });

    this.#renderList();

    const { symbol, amount } = this.#money(this.#subtotal());
    this.els.total.forEach((el) => {
      el.innerHTML = '';
      if (symbol) {
        const s = document.createElement('span');
        s.className = 'dm-b__cur';
        s.textContent = symbol;
        el.appendChild(s);
      }
      const a = document.createElement('span');
      a.textContent = amount;
      el.appendChild(a);
    });

    const ready = filled >= this.minPacks;
    const label = ready
      ? this.strings.ready || 'Add bundle to cart'
      : (this.strings.needMore || 'Pick [n] more to reach min. [min]')
          .replace('[n]', String(remaining))
          .replace('[min]', String(this.minPacks));

    this.els.submit.forEach((el) => {
      el.disabled = !ready;
      if (!el.hasAttribute('data-dm-static-label')) el.textContent = label;
    });

    if (this.els.live) {
      this.els.live.textContent = (this.strings.progress || '[n] packs, [total]')
        .replace('[n]', String(filled))
        .replace('[total]', `${symbol}${amount}`);
    }
  }

  #renderList() {
    const { list, empty } = this.els;
    if (!list) return;

    list.querySelectorAll('[data-dm-row]').forEach((r) => r.remove());

    if (this.selection.size === 0) {
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;

    this.selection.forEach((qty, id) => {
      const row = this.#rowFor(id);
      if (!row) return;

      const wrap = document.createElement('div');
      wrap.className = 'dm-b__side-row';
      wrap.setAttribute('data-dm-row', '');

      const name = document.createElement('span');
      name.className = 'dm-b__side-row-name';
      name.textContent = row.querySelector('.dm-b__name')?.textContent?.trim() || '';

      const q = document.createElement('span');
      q.className = 'dm-b__side-row-qty';
      q.textContent = `×${qty}`;

      wrap.append(name, q);
      list.appendChild(wrap);
    });
  }

  async #submit() {
    if (this.#filled() < this.minPacks) return;

    const { submit, error } = this.els;
    error.forEach((el) => {
      el.textContent = '';
    });
    submit.forEach((el) => {
      el.disabled = true;
    });

    const bundleId = `bp-${Date.now()}`;
    const bundleLabel = this.strings.bundleProperty || 'Bulk Packs bundle';

    const items = [];
    this.selection.forEach((qty, id) => {
      items.push({
        id: Number(id),
        quantity: qty,
        properties: {
          _dm_bundle_id: bundleId,
          _dm_bundle: bundleLabel,
          _dm_min: String(this.minPacks),
        },
      });
    });

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.description || payload.message || 'add_failed');
      }

      // Always reset the builder — the pack now lives in the cart.
      this.selection.clear();
      this.#render();
      this.dispatchEvent(new CustomEvent('dm:bundle-added', { bubbles: true, detail: { bundleId } }));

      // Prefer the drawer: it shows progress toward the order minimum and
      // the free-delivery threshold, which a redirect to /cart would not.
      if (document.querySelector('dm-cart-drawer')) {
        document.dispatchEvent(new CustomEvent('dm:cart-added'));
      } else if (this.dataset.cartRedirect !== 'stay') {
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      }
    } catch (err) {
      const message =
        err.message && err.message !== 'add_failed'
          ? err.message
          : this.strings.genericError || 'Sorry, we could not add this bundle to your cart.';
      error.forEach((el) => {
        el.textContent = message;
      });
      submit.forEach((el) => {
        el.disabled = false;
      });
    }
  }
}

if (!customElements.get('dm-bulk-packs')) {
  customElements.define('dm-bulk-packs', DMBulkPacks);
}
